import {
  MIN_SEARCH_RADIUS_PX,
  MAX_SEARCH_RADIUS_PX,
  SMALL_REGION_PIXELS,
  LARGE_REGION_PIXELS,
  DEFAULT_LARGE_MASS_FRACTION,
  DEFAULT_SMALL_MASS_FRACTION,
  DEFAULT_SMALL_MAX_OVERDRAW_RATIO,
  DEFAULT_LARGE_MAX_OVERDRAW_RATIO,
  DEFAULT_ALL_OF_LABELS,
  ASSIGNMENT_DISTANCE_SOFTENER_PX,
  CANONICAL_LABELS,
  COLOR_AGNOSTIC_LABELS,
  DETECTION_FILL_MS,
  DETECTION_STROKE_MS,
  SPREAD_REGION_PIXELS,
  LABEL_DEFAULTS,
} from "./constants";
import { findConnectedComponents, quantizeCanvas } from "./imageOps";
import { redraw } from "./strokeRender";

const CANONICAL_RANK = new Map(
  CANONICAL_LABELS.map((label, i) => [label.toLowerCase(), i]),
);

// Module-singleton offscreen canvas for rasterizing strokes during
// scoring. Reused across calls — score recomputes once per stroke
// commit, so allocating a fresh canvas each time would be wasteful.
// Sized to the reference's canvas dims on demand.
let _scoringCanvas = null;
function getScoringCanvas(width, height) {
  if (_scoringCanvas == null) {
    _scoringCanvas = document.createElement("canvas");
  }
  if (_scoringCanvas.width !== width) _scoringCanvas.width = width;
  if (_scoringCanvas.height !== height) _scoringCanvas.height = height;
  return _scoringCanvas;
}

// Linear interp on `pixelCount` between SMALL_REGION_PIXELS and
// LARGE_REGION_PIXELS, clamped at the endpoints. Both the search-radius
// curve (small region → big radius) and the mass-fraction curve (small
// region → lenient fraction) live on this axis, so they stay coherent.
function interpRegionSize(pixelCount, valAtSmall, valAtLarge) {
  if (pixelCount <= SMALL_REGION_PIXELS) return valAtSmall;
  if (pixelCount >= LARGE_REGION_PIXELS) return valAtLarge;
  const t =
    (pixelCount - SMALL_REGION_PIXELS) /
    (LARGE_REGION_PIXELS - SMALL_REGION_PIXELS);
  return valAtSmall + (valAtLarge - valAtSmall) * t;
}

// Inverse-size curve: small regions get big search boxes, big regions get
// small ones. See constants.js for the rationale. `scale` multiplies the
// final radius (per-region overrides included) so debug tuning can fan the
// whole curve in/out at once. Pass effectivePixelCount when a label
// downrank applies (LABEL_DEFAULTS.sizeFactor) — neck consults the curve
// at its scaled-down size, getting a more generous search radius
// alongside the more lenient mass fraction.
function searchRadiusFor(region, scale, effectivePixelCount) {
  const base =
    typeof region.searchRadius === "number"
      ? region.searchRadius
      : interpRegionSize(
          effectivePixelCount ?? region.pixelCount ?? 0,
          MAX_SEARCH_RADIUS_PX,
          MIN_SEARCH_RADIUS_PX,
        );
  return base * scale;
}

function massFractionFor(pixelCount, smallFraction, largeFraction) {
  return interpRegionSize(pixelCount, smallFraction, largeFraction);
}

// Region IDs in reference.json are positional (`r-001` = biggest region
// in that marc, `r-002` = second biggest, …) and so they collide across
// marcs — every marc has its own `r-001`. That bites the speedrun
// runtime: MarcEngine and DetectionGate key state by id, and during the
// brief render window where the engine has been fed the previous marc's
// regions before the new marc's reference loads, shownId can latch onto
// an id that the *next* marc happens to reuse. The follow-up update with
// the new marc's regions then early-returns ("id matches, no-op"),
// `onTargetChange` doesn't fire, and `setActiveColorIndex` skips — so
// the brush stays on the prior marc's color but in the new marc's
// palette. Background-tab timer throttling makes the race much wider
// and was how this got noticed.
//
// Fix: the on-disk ids stay positional (saveReference is unchanged, no
// migration), but every id that leaves scoreDrawing is namespaced with
// the marc id, so A's `001::r-001` is a different string than B's
// `004::r-001`. Engines, gates, and the panel still treat ids as
// opaque keys; they just see longer strings.
function namespacedRegionId(reference, regionId) {
  return `${reference.marc.id}::${regionId}`;
}

// Same shape as the mass-fraction and search-radius curves: small
// regions get a much more generous overdraw multiplier (a tiny
// reference mass swamps any normal pen stroke at a flat 3×, but the
// flood-fill failure mode being detected is enormous in absolute
// pixels and trips at any reasonable ratio). Large regions stay at
// the strict 3× — a sweater + accidental whole-canvas fill is still
// many multiples of the reference mass.
function maxOverdrawRatioFor(pixelCount, smallRatio, largeRatio) {
  return interpRegionSize(pixelCount, smallRatio, largeRatio);
}

export const SCORING_DEFAULTS = {
  largeMassFraction: DEFAULT_LARGE_MASS_FRACTION,
  smallMassFraction: DEFAULT_SMALL_MASS_FRACTION,
  smallMaxOverdrawRatio: DEFAULT_SMALL_MAX_OVERDRAW_RATIO,
  largeMaxOverdrawRatio: DEFAULT_LARGE_MAX_OVERDRAW_RATIO,
  searchRadiusScale: 1,
  distanceSoftener: ASSIGNMENT_DISTANCE_SOFTENER_PX,
  // Detection gate (consumed by SpeedrunEngine, not scoreDrawing). Lives
  // here so the debug panel surfaces all play-loop knobs in one place.
  detectionFillMs: DETECTION_FILL_MS,
  detectionStrokeMs: DETECTION_STROKE_MS,
};

function normalizeLabel(label) {
  return (label ?? "").trim().toLowerCase();
}

const DEFAULT_ALL_OF_SET = new Set(
  DEFAULT_ALL_OF_LABELS.map((s) => s.toLowerCase())
);

// Groups (regions sharing a label) score in one of two modes:
//   "all" — each subregion must individually clear its own threshold.
//           Group score = average of subregion scores (so two-of-eyes is
//           50% when one eye is fully drawn). Group detected = every
//           subregion detected.
//   "any" — hits pool across subregions; threshold is the largest
//           subregion's threshold, so filling one subregion alone clears
//           the group, AND a single stroke spanning multiple subregions
//           clears the group via summed hits.
//
// Resolution order: explicit `reference.labelGroups[label]` wins, then the
// hard-coded DEFAULT_ALL_OF_LABELS list (eyes/eyebrows/pupils), else "any".
function resolveGroupMode(labelKey, labelGroups) {
  const explicit = labelGroups?.[labelKey];
  if (explicit === "all" || explicit === "any") return explicit;
  return DEFAULT_ALL_OF_SET.has(labelKey) ? "all" : "any";
}

function isColorAgnostic(region, agnosticSet) {
  if (region.matchAnyColor === true) return true;
  return agnosticSet.has(normalizeLabel(region.label));
}

function bboxesIntersect(a, b) {
  return !(
    a.x + a.w <= b.x0 ||
    a.x >= b.x1 ||
    a.y + a.h <= b.y0 ||
    a.y >= b.y1
  );
}

export function scoreDrawing({ strokes, palette, reference, overrides }) {
  if (!reference) {
    return { regions: [], overall: 0, labeledCount: 0, detectedCount: 0 };
  }

  const largeMassFraction =
    overrides?.largeMassFraction ?? SCORING_DEFAULTS.largeMassFraction;
  const smallMassFraction =
    overrides?.smallMassFraction ?? SCORING_DEFAULTS.smallMassFraction;
  const smallMaxOverdrawRatio =
    overrides?.smallMaxOverdrawRatio ?? SCORING_DEFAULTS.smallMaxOverdrawRatio;
  const largeMaxOverdrawRatio =
    overrides?.largeMaxOverdrawRatio ?? SCORING_DEFAULTS.largeMaxOverdrawRatio;
  const radiusScale =
    overrides?.searchRadiusScale ?? SCORING_DEFAULTS.searchRadiusScale;
  const distanceSoftener =
    overrides?.distanceSoftener ?? SCORING_DEFAULTS.distanceSoftener;
  const labelGroups = reference.labelGroups ?? null;

  // Rasterize strokes to the scoring canvas. Same redraw() the on-screen
  // canvas uses, so the pixel result is byte-identical to what the player
  // sees — quantize/connected-components downstream stay consistent
  // between scoring and the saved reference's region extraction.
  const canvasW = reference.canvas.width;
  const canvasH = reference.canvas.height;
  const canvas = getScoringCanvas(canvasW, canvasH);
  redraw(canvas, strokes ?? [], palette);

  const { indices, width, height } = quantizeCanvas(canvas, palette);

  const agnosticSet = new Set(
    COLOR_AGNOSTIC_LABELS.map((s) => s.toLowerCase())
  );
  const labeled = reference.drawing.regions.filter(
    (r) => normalizeLabel(r.label).length > 0
  );

  // Precompute each labeled region's dilated search box + per-region settings.
  // `assignedHits` accumulates as we credit components during the match loop.
  const subs = labeled.map((r) => {
    // Per-label difficulty downrank — see LABEL_DEFAULTS in
    // constants.js. effectivePixelCount is what the curve sees for
    // both mass fraction (later, in score computation) and search
    // radius (here). pixelCount itself is unchanged for everything
    // else (display, overdraw budget, halfPixels).
    const sizeFactor =
      LABEL_DEFAULTS[normalizeLabel(r.label)]?.sizeFactor ?? 1;
    const effectivePixelCount = r.pixelCount * sizeFactor;
    const radius = searchRadiusFor(r, radiusScale, effectivePixelCount);
    // Spread split: subregions above SPREAD_REGION_PIXELS get an
    // automatic left/right (or top/bottom) split along the longer
    // axis. Each half has its own threshold (proportional to the
    // mask's pixelCount in that half, from saved halfPixels — falls
    // back to 50/50 if missing) and is independently capped at
    // contribution time. This forces "draw across the whole region"
    // without requiring a multi-subregion JSON layout.
    const useSpread = r.pixelCount > SPREAD_REGION_PIXELS;
    const splitAxis = useSpread ? (r.bbox.w >= r.bbox.h ? "x" : "y") : null;
    const splitMid = useSpread
      ? splitAxis === "x"
        ? r.bbox.x + r.bbox.w / 2
        : r.bbox.y + r.bbox.h / 2
      : null;
    const halfPixelCounts = useSpread
      ? r.halfPixels?.counts ?? [r.pixelCount / 2, r.pixelCount / 2]
      : null;
    return {
      region: r,
      x0: Math.max(0, Math.floor(r.bbox.x - radius)),
      y0: Math.max(0, Math.floor(r.bbox.y - radius)),
      x1: Math.min(width, Math.ceil(r.bbox.x + r.bbox.w + radius)),
      y1: Math.min(height, Math.ceil(r.bbox.y + r.bbox.h + radius)),
      cx: r.bbox.x + r.bbox.w / 2,
      cy: r.bbox.y + r.bbox.h / 2,
      matchAnyColor: isColorAgnostic(r, agnosticSet),
      assignedHits: 0,
      // Total size of player components attributed to this subregion
      // (full component pixel counts, not bbox-clipped overlap). Used
      // for overdraw detection — see maxOverdrawRatioFor curve. A
      // single big spilled component blows this up even if only part
      // of it lands in the bbox.
      assignedSize: 0,
      // Spread-split state. splitAxis null = single-cap mode.
      splitAxis,
      splitMid,
      halfPixelCounts,
      halfHits: useSpread ? [0, 0] : null,
      // Label downrank — passed through to mass fraction calc below.
      sizeFactor,
      effectivePixelCount,
    };
  });

  // Regions with the same label merge into one scoring group. Useful when a
  // single semantic thing (e.g. a jacket visible on both sides of Marc's
  // chest) was traced as two disconnected regions. Subregions keep their
  // own dilated bboxes so position-sensitivity is preserved per-piece; mode
  // (all-of vs any-of, see resolveGroupMode) decides how subregion hits
  // combine into the group's score.
  const groupMap = new Map();
  for (const s of subs) {
    const key = normalizeLabel(s.region.label);
    if (!groupMap.has(key)) {
      groupMap.set(key, {
        label: s.region.label.trim(),
        labelKey: key,
        mode: resolveGroupMode(key, labelGroups),
        subregions: [],
        totalPixelCount: 0,
        // Sum of comp.pixels.length over each component for which this
        // group was bestGroup. Counted ONCE per component (not once per
        // overlapping subregion), so any-of mode evaluates overdraw
        // against the group's pooled mass instead of the smallest
        // subregion's mass — see "any-of overdraw" below.
        groupAssignedSize: 0,
      });
    }
    const g = groupMap.get(key);
    g.subregions.push(s);
    g.totalPixelCount += s.region.pixelCount;
  }
  // Sort groups by canonical-label order first (so the player draws
  // foundational regions before details — see CANONICAL_LABELS in
  // constants.js), then by pixel count for anything unlisted. labelKey
  // is already lowercased by normalizeLabel.
  const groups = Array.from(groupMap.values()).sort((a, b) => {
    const ra = CANONICAL_RANK.get(a.labelKey) ?? Infinity;
    const rb = CANONICAL_RANK.get(b.labelKey) ?? Infinity;
    if (ra !== rb) return ra - rb;
    return b.totalPixelCount - a.totalPixelCount;
  });

  const components = findConnectedComponents(indices, width, height);

  // For each player component, pick the single best-matching group, then
  // credit subregions within that group. Crediting differs by mode:
  //   all-of → only the single best-matching subregion, because dilated
  //            bboxes of small features (eyes, eyebrows) overlap heavily,
  //            and crediting all of them would let one eyebrow stroke
  //            satisfy both eyebrow subregions.
  //   any-of → every overlapping subregion (current behavior). One stroke
  //            spanning both halves of a sweater is desirable here; minor
  //            double-counting on overlapping bboxes is tolerable and is
  //            tuned out via thresholds, not credit shape.
  //
  // Future tweak for any-of if the leniency feels off: gate multi-subregion
  // credit on the stroke actually *touching* each subregion's bbox (e.g.
  // require ≥N pixels overlap with each), so dilated-bbox-overlap alone
  // doesn't fan out credit. Single stroke spanning both halves still works,
  // but a stroke wholly inside one half stops accidentally crediting its
  // neighbor.
  for (const comp of components) {
    const compCx = comp.bbox.x + comp.bbox.w / 2;
    const compCy = comp.bbox.y + comp.bbox.h / 2;

    let bestGroup = null;
    let bestGroupScore = 0;
    let bestGroupOverlaps = null;

    for (const g of groups) {
      let gScore = 0;
      const overlaps = [];

      for (const s of g.subregions) {
        if (!s.matchAnyColor && s.region.colorIndex !== comp.colorIndex) {
          continue;
        }
        if (!bboxesIntersect(comp.bbox, s)) continue;

        let overlap = 0;
        // Per-half overlap accumulators when this sub uses spread
        // split. Null otherwise — keeps the hot-loop branch-free for
        // the common case.
        const halfOverlap = s.splitAxis ? [0, 0] : null;
        for (let k = 0; k < comp.pixels.length; k++) {
          const i = comp.pixels[k];
          const px = i % width;
          const py = (i - px) / width;
          if (px >= s.x0 && px < s.x1 && py >= s.y0 && py < s.y1) {
            overlap++;
            if (halfOverlap) {
              const pos = s.splitAxis === "x" ? px : py;
              halfOverlap[pos < s.splitMid ? 0 : 1]++;
            }
          }
        }
        if (overlap === 0) continue;

        const dx = compCx - s.cx;
        const dy = compCy - s.cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const sScore = overlap / (1 + dist / distanceSoftener);
        overlaps.push({ s, overlap, halfOverlap, sScore });
        if (sScore > gScore) gScore = sScore;
      }

      if (gScore > bestGroupScore) {
        bestGroupScore = gScore;
        bestGroup = g;
        bestGroupOverlaps = overlaps;
      }
    }

    if (bestGroup && bestGroupOverlaps.length > 0) {
      const compSize = comp.pixels.length;
      bestGroup.groupAssignedSize += compSize;
      if (bestGroup.mode === "all") {
        let pick = bestGroupOverlaps[0];
        for (let i = 1; i < bestGroupOverlaps.length; i++) {
          if (bestGroupOverlaps[i].sScore > pick.sScore) {
            pick = bestGroupOverlaps[i];
          }
        }
        pick.s.assignedHits += pick.overlap;
        pick.s.assignedSize += compSize;
        if (pick.s.halfHits && pick.halfOverlap) {
          pick.s.halfHits[0] += pick.halfOverlap[0];
          pick.s.halfHits[1] += pick.halfOverlap[1];
        }
      } else {
        for (const { s, overlap, halfOverlap } of bestGroupOverlaps) {
          s.assignedHits += overlap;
          s.assignedSize += compSize;
          if (s.halfHits && halfOverlap) {
            s.halfHits[0] += halfOverlap[0];
            s.halfHits[1] += halfOverlap[1];
          }
        }
      }
    }
  }

  const regions = groups.map((g) => {
    // Per-subregion threshold from the curve, keyed on the subregion's own
    // pixelCount so a tiny eye gets the lenient small-region fraction even
    // when paired with a bigger sibling. Per-region `minMassFraction`
    // overrides still win via Math.min.
    //
    // Per-subregion `overdrawn` is only consulted in all-of mode, where
    // each subregion stands alone. Any-of evaluates overdraw at the
    // group level (see below) so a small subregion in a multi-region
    // group doesn't dictate the cap for the whole thing.
    const subResults = g.subregions.map((s) => {
      // Curve consults the EFFECTIVE pixelCount so a label downrank
      // (LABEL_DEFAULTS.sizeFactor) gets the smaller-region's lenient
      // mass fraction. Threshold then uses effectivePixelCount × that
      // fraction — so a 25K neck with sizeFactor=0.4 grades as a 10K
      // region (lower fraction × smaller mass = much smaller required
      // ink). HalfPixelCounts are also scaled by sizeFactor for
      // spread-split subregions, keeping the half thresholds
      // consistent with the whole.
      const curveFraction = massFractionFor(
        s.effectivePixelCount,
        smallMassFraction,
        largeMassFraction
      );
      const fraction =
        typeof s.region.minMassFraction === "number"
          ? Math.min(curveFraction, s.region.minMassFraction)
          : curveFraction;
      const threshold = Math.max(1, s.effectivePixelCount * fraction);

      // cappedHits: how much of this sub's threshold the player has
      // earned. For small/medium subs, just capped at threshold.
      // For spread-split subs, sum of per-half capped contributions
      // — drawing only one half can't earn more than that half's
      // share even if the player overpaints heavily.
      let cappedHits;
      let halfThresholds = null;
      if (s.splitAxis && s.halfHits) {
        const lt = Math.max(1, s.halfPixelCounts[0] * s.sizeFactor * fraction);
        const rt = Math.max(1, s.halfPixelCounts[1] * s.sizeFactor * fraction);
        halfThresholds = [lt, rt];
        cappedHits =
          Math.min(s.halfHits[0], lt) + Math.min(s.halfHits[1], rt);
      } else {
        cappedHits = Math.min(s.assignedHits, threshold);
      }
      const baseScore = Math.min(1, cappedHits / threshold);

      // Curve consults pixelCount (NOT effectivePixelCount): label
      // downrank widens threshold/radius for alignment slack but does
      // not change "did the player accidentally flood-fill?" — the
      // overdraw budget tracks the actual ink mass on the canvas.
      const overdrawRatio =
        typeof s.region.maxOverdrawRatio === "number"
          ? s.region.maxOverdrawRatio
          : maxOverdrawRatioFor(
              s.region.pixelCount,
              smallMaxOverdrawRatio,
              largeMaxOverdrawRatio,
            );
      const overdrawAllowed = s.region.pixelCount * overdrawRatio;
      const subOverdrawn = s.assignedSize > overdrawAllowed;
      const score = subOverdrawn
        ? Math.max(
            0,
            baseScore - (s.assignedSize - overdrawAllowed) / overdrawAllowed,
          )
        : baseScore;

      return {
        sub: s,
        hits: s.assignedHits,
        cappedHits,
        threshold,
        score,
        detected: !subOverdrawn && cappedHits >= threshold,
        overdrawn: subOverdrawn,
        // Debug-only info; ignored by core scoring.
        halfHits: s.halfHits,
        halfThresholds,
        splitAxis: s.splitAxis,
      };
    });

    let hits;
    let required;
    let score;
    let detected;
    let overdrawn;

    if (g.mode === "all") {
      // All-of: each subregion stands on its own. Display hits/required as
      // sums for tooltip continuity, but the user-visible progress is the
      // average of subregion scores so two-of-eyes reads 50% when one is
      // fully drawn. Detection requires every subregion individually clear.
      hits = subResults.reduce((acc, sr) => acc + sr.cappedHits, 0);
      required = subResults.reduce((acc, sr) => acc + sr.threshold, 0);
      score =
        subResults.length > 0
          ? subResults.reduce((acc, sr) => acc + sr.score, 0) /
            subResults.length
          : 0;
      detected = subResults.every((sr) => sr.detected);
      overdrawn = subResults.some((sr) => sr.overdrawn);
    } else {
      // Any-of: subregions are facets of one logical region. Each
      // subregion contributes up to its OWN threshold to the pooled
      // score — past that, drawing more in one part doesn't keep
      // pumping the bar. This is what enforces "every part must be
      // touched" without a binary spread gate: reaching 100% requires
      // every subregion at its threshold, and the bar climbs
      // smoothly as the player covers each part.
      //
      // Why per-sub cap instead of all-of: credit still goes to ALL
      // overlapping subregions per component (see the credit loop),
      // so one stroke that genuinely spans both halves of a split
      // jacket gets credit on both. All-of's "best subregion only"
      // credit would lose that property.
      //
      // Required pools for sum-threshold semantics. Overdraw uses
      // groupAssignedSize / totalPixelCount, summed once per
      // attributed component (not per overlapping subregion).
      hits = subResults.reduce((acc, sr) => acc + sr.cappedHits, 0);
      required = subResults.reduce((acc, sr) => acc + sr.threshold, 0);

      const groupOverdrawRatio = maxOverdrawRatioFor(
        g.totalPixelCount,
        smallMaxOverdrawRatio,
        largeMaxOverdrawRatio,
      );
      const overdrawAllowed = g.totalPixelCount * groupOverdrawRatio;
      overdrawn = g.groupAssignedSize > overdrawAllowed;
      const baseScore = Math.min(1, hits / required);

      score = overdrawn
        ? Math.max(
            0,
            baseScore -
              (g.groupAssignedSize - overdrawAllowed) / overdrawAllowed,
          )
        : baseScore;
      detected = !overdrawn && hits >= required;
    }

    const first = g.subregions[0].region;
    const anyAgnostic = g.subregions.some((s) => s.matchAnyColor);

    // Per-subregion debug breakdown — only used by DebugScoringPanel
    // to make per-region tuning intelligible. Cheap to compute, doesn't
    // change scoring behavior.
    const subRegions = g.subregions.map((s, i) => {
      const sr = subResults[i];
      return {
        id: namespacedRegionId(reference, s.region.id),
        pixelCount: s.region.pixelCount,
        bbox: s.region.bbox,
        searchBbox: { x: s.x0, y: s.y0, w: s.x1 - s.x0, h: s.y1 - s.y0 },
        hits: s.assignedHits,
        cappedHits: sr.cappedHits,
        assignedSize: s.assignedSize,
        colorIndex: s.region.colorIndex,
        // Spread-split debug, null when not applicable.
        splitAxis: s.splitAxis,
        halfHits: s.halfHits,
        halfPixelCounts: s.halfPixelCounts,
        halfThresholds: sr.halfThresholds,
      };
    });

    const overdrawAllowed = Math.round(
      g.totalPixelCount *
        maxOverdrawRatioFor(
          g.totalPixelCount,
          smallMaxOverdrawRatio,
          largeMaxOverdrawRatio,
        ),
    );

    return {
      id: namespacedRegionId(
        reference,
        g.subregions.map((s) => s.region.id).join("+"),
      ),
      label: g.label,
      color: first.color,
      colorIndex: first.colorIndex,
      pixelCount: g.totalPixelCount,
      hits,
      required: Math.round(required),
      score,
      detected,
      overdrawn,
      matchAnyColor: anyAgnostic,
      subCount: g.subregions.length,
      mode: g.mode,
      // Debug fields. assignedSize is group-level for any-of, sum of
      // subregion sizes for all-of (where the group-level pool isn't
      // the threshold for overdraw, but it's still informative).
      assignedSize:
        g.mode === "all"
          ? subRegions.reduce((acc, s) => acc + s.assignedSize, 0)
          : g.groupAssignedSize,
      overdrawAllowed,
      subRegions,
    };
  });

  const detectedCount = regions.filter((r) => r.detected).length;
  const overall = regions.length > 0 ? detectedCount / regions.length : 0;
  return { regions, overall, labeledCount: regions.length, detectedCount };
}
